import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlattener, MatTreeFlatDataSource } from '@angular/material/tree';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

export interface ITreeNode {
  id: number;
  name: string;
  description: string;
  children?: ITreeNode[];
}

interface IFlatNode {
  id: number;
  name: string;
  description: string;
  level: number;
  expandable: boolean;
}

@Component({
  selector: 'app-tree-select-input',
  templateUrl: './tree-select-input.component.html',
  styleUrls: ['./tree-select-input.component.scss']
})
export class TreeSelectInputComponent implements OnInit {
  @Input() descriptionPath: string = null;
  @Input() namePath: string = null;

  @Input() set treeData(value: ITreeNode[]) {
    value = value || [];

    this.dataSource.data = value;
    this.buildTreeForm();
    this.registerTreeFormChanges();
    this.patchTreeForm(this.treeFormInitialData)
  };
  get treeData(): ITreeNode[] {
    return this.dataSource.data;
  }

  @Input() treeDepth: number = 1;

  _treeFormInitialData: BehaviorSubject<any>;
  @Input() set treeFormInitialData(value: number[]) {
    value = value || [];
    this._treeFormInitialData.next(this.mapItemValueIds(value));
  };
  get treeFormInitialData() {
    return this._treeFormInitialData.getValue();
  }

  @Input() treeForm: UntypedFormGroup;
  @Output() treeFormChange: EventEmitter<UntypedFormGroup>;
  @Output() selectionChange: EventEmitter<void>;

  _detailMode: boolean = false;
  @Input() set detailMode(value: boolean) {
    this._detailMode = value;
    if (this._detailMode) {
      this.treeForm.disable();
    }
  };

  private _transformer = (node: ITreeNode, level: number): IFlatNode => {
    return {
      id: node.id,
      name: node.name,
      description: node.description,
      expandable: !!node.children && node.children.length > 0,
      level: level,
    };
  }

  treeControl = new FlatTreeControl<IFlatNode>(node => node.level, node => node.expandable);
  treeFlattener = new MatTreeFlattener(this._transformer, node => node.level, node => node.expandable, node => node.children);
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  constructor(
    private fb: UntypedFormBuilder,
    private translate: TranslateService
  ) {
    this.treeFormChange = new EventEmitter<UntypedFormGroup>();
    this.selectionChange = new EventEmitter();
    this._treeFormInitialData = new BehaviorSubject<any>({});
  }

  ngOnInit() {
    this.registerInitalDataOnChange();
  }

  patchTreeForm(values: any) {
    if (!this.treeForm)
      return;

    this.treeForm.patchValue(values);
  }

  registerInitalDataOnChange() {
    this._treeFormInitialData.subscribe({
      next: x => this.patchTreeForm(x)
    });
  }

  mapItemValueIds(itemIds: number[]) {
    const itemValueForm = {};
    for (const id of itemIds) {
      const treeKey = Object.getOwnPropertyNames(this.treeForm.value)
        .find(x => !!x.match(new RegExp(`^${this.treeDepth}-${id}$`)));

      itemValueForm[treeKey] = true;
    }
    return itemValueForm;
  }

  hasChild = (_: number, node: IFlatNode) => node.expandable;

  buildTreeForm(): void {
    const treeData: ITreeNode[] = this.treeData;
    if (!treeData.length)
      return;

    var form = {};
    var addNodeToForm = (node: ITreeNode, level: number = 0) => {
      form[`${level}-${node.id}`] = [
        { value: null, disabled: !!this._detailMode },
        []
      ];

      if (!!node.children) {
        node.children.forEach(x => addNodeToForm(x, level + 1))
      }
    };

    for (const node of treeData) {
      addNodeToForm(node);
    }

    this.treeForm = this.fb.group(form);
  }

  registerTreeFormChanges(): void {
    if (!this.treeForm)
      return;

    this.treeForm.valueChanges.subscribe({
      next: x => this.treeFormChange.emit(this.treeForm)
    });
    this.treeForm.valueChanges.subscribe({
      next: x => {
        if (this.treeForm.dirty) {
          this.selectionChange.emit();
        }
      }
    });
  }

  getItemDescription(node: IFlatNode): string {
    return !!this.descriptionPath ? this.translate.instant(`${this.descriptionPath}.${node.id}`) : null;
  }

  getItemName(node: IFlatNode): string {
    return !!this.namePath ? this.translate.instant(`${this.namePath}.${node.name.toUpperCase()}`) : node.name;
  }
}
